import { useTranslation } from "react-i18next";
import { useLocale } from "../../hooks/useLocale";
import { formatCountdown, formatTime, initials } from "../../lib/format";
import type { Conversation } from "../../lib/types";
import { Avatar, Empty } from "../ui/Badges";

interface Props {
  conversations: Conversation[];
  selectedId: string | null;
  onSelect: (guardianId: string) => void;
}

export function ChatList({ conversations, selectedId, onSelect }: Props) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  return (
    <div className="space-y-2 bg-gray-50/60 p-3.5">
      <div className="flex items-center justify-between border-b border-gray-200 pb-2">
        <span className="text-xs font-black uppercase text-cosmic-navy">{t("whatsapp.activeChats")}</span>
        <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-800">{conversations.length}</span>
      </div>
      {conversations.length === 0 && <Empty>{t("whatsapp.noChats")}</Empty>}
      {conversations.map((c) => {
        const selected = c.guardian.id === selectedId;
        const s = c.session;
        const overdue = s && s.remainingSeconds < 0;
        return (
          <button
            key={c.guardian.id}
            type="button"
            aria-current={selected}
            onClick={() => onSelect(c.guardian.id)}
            className={`w-full rounded-xl border p-2.5 text-start transition ${selected ? "border-purple-200 bg-purple-50 shadow-sm" : "border-gray-200 bg-white hover:bg-gray-100"}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex min-w-0 items-center gap-2">
                <Avatar text={initials(c.guardian.fullName)} />
                <div className="min-w-0">
                  <div className="truncate text-xs font-extrabold text-cosmic-navy">{c.guardian.fullName}</div>
                  <p className={`truncate text-[11px] ${c.awaitingReply ? "font-semibold text-cosmic-purple" : "text-gray-500"}`}>
                    {c.lastMessage.direction === "out" ? "↗ " : "↙ "}
                    {c.lastMessage.body}
                  </p>
                </div>
              </div>
              {c.awaitingReply && <span className="mt-1 h-2.5 w-2.5 flex-shrink-0 rounded-full bg-cosmic-purple" aria-label={t("whatsapp.awaitingReply")} />}
            </div>
            <div className="mt-1 flex items-center justify-between text-[10px] text-gray-400">
              <span className="num font-mono">{s ? `${s.wristbandTag} (${s.kidName})` : c.guardian.phone}</span>
              <span className="flex items-center gap-1.5">
                {s && <span className={`num font-black ${overdue ? "text-red-600" : s.remainingSeconds <= s.warnMinutes * 60 ? "text-solar-orange" : "text-emerald-600"}`}>{overdue ? "+" : ""}{formatCountdown(s.remainingSeconds, locale)}</span>}
                <span className="num">{formatTime(c.lastMessage.createdAt, locale)}</span>
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
}
